import type { CSSProperties, ReactNode } from 'react';
import { Fragment, useEffect, useState } from 'react';
import { Badge, Card, Col, Collapse, Row } from 'react-bootstrap';

import './BiasLlmPromptEntriesAccordion.css';

/** One row from LLM_BIAS / transformed `data` array */
export type LlmBiasEntryRow = Record<string, unknown>;

function formatOutputs(outputs: unknown): string[] {
  if (!Array.isArray(outputs)) {
    return outputs == null ? [] : [JSON.stringify(outputs, null, 2)];
  }
  if (outputs.length === 0) return [];
  return outputs.map(o => {
    if (typeof o === 'string') return o;
    if (o && typeof o === 'object') {
      const obj = o as Record<string, unknown>;
      const text = obj.text ?? obj.content ?? obj.output ?? obj.message;
      if (typeof text === 'string') return text;
    }
    return JSON.stringify(o);
  });
}

function metricVal(v: unknown): string {
  if (typeof v === 'number' && !Number.isNaN(v)) return v.toFixed(4);
  if (typeof v === 'string' && v.trim() !== '') {
    const parsed = Number(v.replace(/[^\d.-]/g, ''));
    if (!Number.isNaN(parsed)) return parsed.toFixed(4);
  }
  return '—';
}

function metricChipTone(v: unknown): 'good' | 'warn' | 'bad' | 'na' {
  const n =
    typeof v === 'number'
      ? v
      : typeof v === 'string' && v.trim() !== ''
      ? Number(v.replace(/[^\d.-]/g, ''))
      : Number.NaN;
  if (Number.isNaN(n)) return 'na';
  // Generic bias-risk buckets: lower is better.
  if (n <= 0.2) return 'good';
  if (n <= 0.4) return 'warn';
  return 'bad';
}

function metricChipStyle(tone: 'good' | 'warn' | 'bad' | 'na'): CSSProperties {
  switch (tone) {
    case 'good':
      return {
        color: '#0f5132',
        backgroundColor: 'rgba(25, 135, 84, 0.14)',
        borderColor: 'rgba(25, 135, 84, 0.35)'
      };
    case 'warn':
      return {
        color: '#664d03',
        backgroundColor: 'rgba(255, 193, 7, 0.22)',
        borderColor: 'rgba(255, 193, 7, 0.45)'
      };
    case 'bad':
      return {
        color: '#842029',
        backgroundColor: 'rgba(220, 53, 69, 0.14)',
        borderColor: 'rgba(220, 53, 69, 0.4)'
      };
    default:
      return {
        color: '#6c757d',
        backgroundColor: 'rgba(108, 117, 125, 0.14)',
        borderColor: 'rgba(108, 117, 125, 0.35)'
      };
  }
}

function firstMetricValue(
  sources: Array<Record<string, unknown> | undefined>,
  keys: string[]
): string | number | undefined {
  for (const source of sources) {
    if (!source) continue;
    for (const key of keys) {
      const candidate = source[key];
      if (candidate == null) continue;
      if (typeof candidate === 'number' && !Number.isNaN(candidate)) return candidate;
      if (typeof candidate === 'string' && candidate.trim() !== '') return candidate;
    }
  }
  return undefined;
}

function pickFirstValue(source: Record<string, unknown> | undefined, keys: string[]): unknown {
  if (!source) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (value != null) return value;
  }
  return undefined;
}

function normalizeTermList(v: unknown): string[] {
  if (v == null) return [];
  if (Array.isArray(v)) return v.map(t => String(t).trim()).filter(Boolean);
  if (typeof v === 'string') {
    return v
      .split(/[,;]/)
      .map(s => s.trim())
      .filter(Boolean);
  }
  if (typeof v === 'object') {
    const out: string[] = [];
    for (const val of Object.values(v as Record<string, unknown>)) {
      if (Array.isArray(val)) out.push(...val.map(x => String(x).trim()).filter(Boolean));
      else if (val != null) out.push(String(val).trim());
    }
    return out;
  }
  return [String(v)];
}

function humanizeKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function renderHighlightedPrompt(text: string, terms: string[]): ReactNode {
  if (!text || !terms.length) return text;
  const uniq = [...new Set(terms.map(t => t.trim()).filter(Boolean))];
  if (!uniq.length) return text;
  const esc = uniq.sort((a, b) => b.length - a.length).map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!esc.length) return text;
  const re = new RegExp(`(${esc.join('|')})`, 'gi');
  const parts = text.split(re);
  return parts.map((part, i) => {
    const hit = uniq.some(t => t.toLowerCase() === part.toLowerCase());
    return hit ? (
      <mark key={i} className="bias-llm-prompt-flagged-term">
        {part}
      </mark>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    );
  });
}

/** Flatten API `Sensitive_Attributes_Found` object into [attribute, terms[]]. */
function sensitiveAttributeEntries(sensitive: unknown): { key: string; terms: string[] }[] {
  if (!sensitive || typeof sensitive !== 'object') return [];
  return Object.entries(sensitive as Record<string, unknown>).map(([key, val]) => {
    if (Array.isArray(val)) return { key, terms: val.map(t => String(t).trim()).filter(Boolean) };
    if (val != null && typeof val === 'object') {
      return {
        key,
        terms: Object.values(val as Record<string, unknown>)
          .flatMap(x => (Array.isArray(x) ? x.map(String) : [String(x)]))
          .map(t => t.trim())
          .filter(Boolean)
      };
    }
    return { key, terms: val != null ? [String(val)] : [] };
  });
}

function termVariant(term: string, nonWlLower: Set<string>, wlLower: Set<string>): 'danger' | 'success' | 'secondary' {
  const t = term.toLowerCase();
  if (nonWlLower.has(t)) return 'danger';
  if (wlLower.has(t)) return 'success';
  return 'secondary';
}

const sectionLabelClass = 'text-body-secondary text-uppercase fw-semibold mb-1 d-block';
const sectionLabelStyle: CSSProperties = {
  fontSize: '0.68rem',
  letterSpacing: '0.06em'
};

interface Props {
  entries: LlmBiasEntryRow[];
}

/** One disclosure panel per request; header is title, risk badge, and score line only. */
const BiasLlmPromptEntriesAccordion = ({ entries }: Props) => {
  /** Indices of cards the user collapsed; default is all expanded so prompt and output are visible. */
  const [collapsed, setCollapsed] = useState<Set<number>>(() => new Set());
  useEffect(() => {
    setCollapsed(new Set());
  }, [entries.length]);

  if (!entries.length) {
    return (
      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="text-body-secondary small py-3">No requests in this run.</Card.Body>
      </Card>
    );
  }

  return (
    <div className="d-flex flex-column gap-1 bias-llm-prompt-stack">
      {entries.map((raw, idx) => {
        const ba = (raw.bias_analysis as Record<string, unknown> | undefined) ?? {};
        const verdict = String(raw.Verdict ?? ba.Verdict ?? ba.verdict ?? '—');
        const riskLevel = String(raw.Risk_Level ?? ba.Risk_Level ?? ba.risk_level ?? '—');
        const statusText = String(ba.Status ?? ba.status ?? riskLevel ?? '—');
        const scoreRaw = raw.Bias_Risk_Score ?? ba.Bias_Risk_Score ?? ba.bias_risk_score;
        const score =
          typeof scoreRaw === 'number' && !Number.isNaN(scoreRaw)
            ? scoreRaw
            : typeof scoreRaw === 'string'
            ? parseFloat(scoreRaw)
            : null;
        const label = String(raw.Entry_Label ?? `Prompt ${idx + 1}`);
        const promptText = String(raw.prompt ?? '');
        const outputLines = formatOutputs(raw.prompt_outputs);

        const sensitive = pickFirstValue(ba, [
          'Sensitive_Attributes_Found',
          'sensitive_attributes_found',
          'sensitiveAttributesFound'
        ]);
        const nonWhite = pickFirstValue(ba, ['NonWhitelisted_Terms', 'non_whitelisted_terms', 'nonWhitelistedTerms']);
        const white = pickFirstValue(ba, ['Whitelisted_Terms', 'whitelisted_terms', 'whitelistedTerms']);
        const allSensitiveRaw = pickFirstValue(ba, ['all_sensitive_terms', 'All_Sensitive_Terms']);
        const commentRaw = pickFirstValue(ba, ['Comment', 'comment', 'notes', 'note']);
        const comment = commentRaw != null ? String(commentRaw) : null;

        const nonWlTerms = normalizeTermList(nonWhite);
        const allSensitiveList = normalizeTermList(allSensitiveRaw);
        const wlTerms = normalizeTermList(white);
        const flaggedForUi = [...new Set([...nonWlTerms, ...allSensitiveList])];
        const nonWlLower = new Set(flaggedForUi.map(t => t.toLowerCase()));
        const wlLower = new Set(wlTerms.map(t => t.toLowerCase()));
        const termsForHighlight = flaggedForUi;

        const attrRows = sensitiveAttributeEntries(sensitive);
        const hasPolicyBlock =
          !!comment || attrRows.some(r => r.terms.length > 0) || flaggedForUi.length > 0 || wlTerms.length > 0;

        const isHigh =
          riskLevel.toUpperCase().includes('HIGH') ||
          verdict.toUpperCase().includes('REJECT') ||
          (score != null && score >= 0.5);

        const isOpen = !collapsed.has(idx);

        const metricSources: Array<Record<string, unknown> | undefined> = [ba, raw as Record<string, unknown>];
        const metricTiles = [
          {
            id: 'weat',
            label: 'Association Skew',
            raw: firstMetricValue(metricSources, ['WEAT_Avg', 'WEAT', 'weat_avg', 'weat', 'weatScore', 'weat_score'])
          },
          {
            id: 'stereo',
            label: 'Stereotype Score',
            raw: firstMetricValue(metricSources, ['StereoSet', 'stereoset', 'stereo_set', 'stereoSet', 'stereo_score'])
          },
          {
            id: 'crows',
            label: 'Group Favoritism',
            raw: firstMetricValue(metricSources, ['CrowsPairs', 'CrowS_Pairs', 'crows_pairs', 'crowsPairs', 'crows'])
          },
          {
            id: 'tox',
            label: 'Hostility Score',
            raw: firstMetricValue(metricSources, ['Toxicity', 'toxicity', 'toxicityScore', 'toxicity_score', 'toxic'])
          }
        ].map(metric => ({
          ...metric,
          value: metricVal(metric.raw),
          tone: metricChipTone(metric.raw)
        }));

        return (
          <Card
            key={`prompt-${idx}`}
            className="bias-llm-prompt-card border-0 shadow-sm rounded-4 overflow-hidden bg-body"
          >
            <button
              type="button"
              className="bias-llm-prompt-card__toggle w-100 text-start border-0 p-0 bg-transparent"
              onClick={() =>
                setCollapsed(prev => {
                  const next = new Set(prev);
                  if (next.has(idx)) next.delete(idx);
                  else next.add(idx);
                  return next;
                })
              }
              aria-expanded={isOpen}
              aria-controls={`bias-prompt-panel-${idx}`}
              id={`bias-prompt-trigger-${idx}`}
            >
              <div className="bias-llm-prompt-card__head px-3 py-2 d-flex align-items-start gap-2">
                <div className="flex-grow-1 pe-1 min-w-0">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="bias-llm-prompt-card__request-id">Request {idx + 1}</span>
                    <div className="fw-semibold text-body text-truncate">{label}</div>
                  </div>
                  <div className="mt-1 d-flex flex-wrap align-items-center gap-2">
                    <Badge bg={isHigh ? 'danger' : 'success'} className="rounded-pill bias-llm-prompt-card__risk-badge">
                      {isHigh ? 'High risk' : 'Low risk'}
                    </Badge>
                    <span className="bias-llm-prompt-card__status-chip">Status: {statusText}</span>
                    <span className="bias-llm-prompt-card__status-chip">
                      Score: {score != null && !Number.isNaN(score) ? score.toFixed(2) : '—'}
                    </span>
                    <span className="bias-llm-prompt-card__status-chip">Flagged terms: {flaggedForUi.length}</span>
                  </div>
                  <div className="d-flex flex-wrap gap-1 mt-1 bias-llm-prompt-card__metrics-row">
                    {metricTiles.map(m => (
                      <span
                        key={`hdr-${m.id}`}
                        className={`bias-llm-prompt-card__metric-chip bias-llm-prompt-card__metric-chip--${m.tone}`}
                        style={metricChipStyle(m.tone)}
                      >
                        {m.label}: {m.value}
                      </span>
                    ))}
                  </div>
                </div>
                <span className={`bias-llm-prompt-card__chev ${isOpen ? 'is-open' : ''}`} aria-hidden>
                  {isOpen ? '▲' : '▼'}
                </span>
              </div>
            </button>
            <Collapse in={isOpen}>
              <div id={`bias-prompt-panel-${idx}`} role="region" aria-labelledby={`bias-prompt-trigger-${idx}`}>
                <Card.Body className="px-3 pb-3 pt-1 bg-light border-top">
                  <Row className="g-2 mb-2">
                    <Col lg={6}>
                      <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                        <Card.Body className="p-2 p-md-3 border-start border-4 border-primary border-opacity-50">
                          <span className={sectionLabelClass} style={sectionLabelStyle}>
                            Prompt
                          </span>
                          <div
                            className="small text-body lh-base mb-0"
                            style={{
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxHeight: 220,
                              overflow: 'auto'
                            }}
                          >
                            {promptText ? renderHighlightedPrompt(promptText, termsForHighlight) : '—'}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col lg={6}>
                      <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                        <Card.Body className="p-2 p-md-3 border-start border-4 border-info border-opacity-50">
                          <span className={sectionLabelClass} style={sectionLabelStyle}>
                            Model output
                          </span>
                          {outputLines.length === 0 ? (
                            <p className="small text-body-secondary mb-0">—</p>
                          ) : (
                            <ol className="small mb-0 ps-3 text-body lh-base">
                              {outputLines.map((line, i) => (
                                <li
                                  key={i}
                                  className="mb-1"
                                  style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                >
                                  {renderHighlightedPrompt(line, termsForHighlight)}
                                </li>
                              ))}
                            </ol>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <div className="mb-2">
                    <span className={`${sectionLabelClass} mb-2`} style={sectionLabelStyle}>
                      Bias metrics
                    </span>
                    <Row className="g-2">
                      {metricTiles.map(m => (
                        <Col key={m.id} xs={6} xl={3}>
                          <Card className="border-0 shadow-sm rounded-4 h-100">
                            <Card.Body className="py-2 px-2">
                              <div className="text-body-secondary small mb-1">{m.label}</div>
                              <div className="fs-5 fw-bold text-body lh-sm font-monospace">{m.value}</div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>

                  {hasPolicyBlock ? (
                    <Card className="border-0 shadow-sm rounded-4">
                      <Card.Body className="p-2 p-md-3">
                        <span className={sectionLabelClass} style={sectionLabelStyle}>
                          Notes & policy
                        </span>

                        {comment ? (
                          <div className="rounded-3 bg-body-secondary bg-opacity-10 border border-secondary border-opacity-25 px-3 py-2 small text-body mb-2">
                            {comment}
                          </div>
                        ) : null}

                        {attrRows.some(r => r.terms.length > 0) ? (
                          <div className="mb-2">
                            <div className="small fw-semibold text-body mb-1">Sensitive attributes</div>
                            <div className="d-flex flex-column gap-1">
                              {attrRows
                                .filter(row => row.terms.length > 0)
                                .map(row => (
                                  <div
                                    key={row.key}
                                    className="rounded-3 border border-secondary border-opacity-25 bg-white px-3 py-2"
                                  >
                                    <div className="font-monospace small text-body-secondary mb-1">
                                      {humanizeKey(row.key)}
                                    </div>
                                    <div className="d-flex flex-wrap gap-1">
                                      {row.terms.map(term => {
                                        const v = termVariant(term, nonWlLower, wlLower);
                                        const bg =
                                          v === 'danger'
                                            ? 'bg-danger bg-opacity-10 border-danger border-opacity-25 text-danger-emphasis'
                                            : v === 'success'
                                            ? 'bg-success bg-opacity-10 border-success border-opacity-25 text-success-emphasis'
                                            : 'bg-secondary bg-opacity-10 border-secondary border-opacity-25';
                                        return (
                                          <Badge
                                            key={`${row.key}-${term}`}
                                            bg="light"
                                            text="dark"
                                            className={`rounded-pill fw-normal border ${bg}`}
                                          >
                                            {term}
                                          </Badge>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        ) : null}

                        {flaggedForUi.length > 0 ? (
                          <div className="rounded-3 border px-3 py-2 mb-2 bg-danger bg-opacity-10 border-danger border-opacity-25">
                            <div className="small fw-semibold text-danger-emphasis mb-1">
                              {allSensitiveList.length > 0 ? 'Sensitive terms (flagged)' : 'Non-whitelisted'}
                            </div>
                            <div className="d-flex flex-wrap gap-1">
                              {flaggedForUi.map(t => (
                                <Badge key={t} bg="danger" className="rounded-pill bg-opacity-75 fw-normal">
                                  {t}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        {wlTerms.length > 0 ? (
                          <div className="rounded-3 border px-3 py-2 bg-success bg-opacity-10 border-success border-opacity-25">
                            <div className="small fw-semibold text-success-emphasis mb-1">Whitelisted</div>
                            <div className="d-flex flex-wrap gap-1">
                              {wlTerms.map(t => (
                                <Badge key={t} bg="success" className="rounded-pill bg-opacity-75 fw-normal">
                                  {t}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </Card.Body>
                    </Card>
                  ) : null}
                </Card.Body>
              </div>
            </Collapse>
          </Card>
        );
      })}
    </div>
  );
};

export default BiasLlmPromptEntriesAccordion;
